import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface PolicyRow {
  readonly policyId: string;
  readonly policyCode: string;
  readonly title: string;
  readonly policyType: string;
  readonly version: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHED', 'REJECTED', 'ARCHIVED', 'RETIRED', 'OBSOLETE'];
const POLICY_TYPES = ['', 'CORPORATE', 'HR', 'FINANCE', 'IT', 'SECURITY', 'COMPLIANCE', 'OPERATIONAL', 'LEGAL'];
const NEW_POLICY_TYPES = ['CORPORATE', 'HR', 'FINANCE', 'IT', 'SECURITY', 'COMPLIANCE', 'OPERATIONAL', 'LEGAL'];
const TERMINAL = ['PUBLISHED', 'OBSOLETE', 'RETIRED'];

/**
 * M35 — Policy Repository (§33). Register of corporate policies (T465) across
 * the 8 policy types, carrying the §8 governed lifecycle (DRAFT → PUBLISHED)
 * and the current version. Filterable by policy type, status and free-text
 * search. Tenant isolation is enforced server-side (LAW 4).
 */
export function PolicyRepository(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<PolicyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [policyType, setPolicyType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState(NEW_POLICY_TYPES[0]);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (policyType) params.set('policyType', policyType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: PolicyRow[]; total: number }>(`/v1/kms/policies${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, policyType, status, q]);

  useEffect(() => load(), [load]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m35.policy.titleRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/policies`, {
        method: 'POST',
        body: { title: newTitle.trim(), policyType: newType },
        token,
      });
      setNotice(t('m35.common.created', { name: newTitle.trim() }));
      setNewTitle('');
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.policy.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: PolicyRow, op: string, label: string, perm: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/policies/${r.policyId}/${op}`, { method: 'POST', body, token });
      setNotice(t('m35.common.notice', { code: r.policyCode, label }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.common.notAuthorisedAction', { perm }));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r: PolicyRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      await apiRequest(`/v1/kms/policies/${r.policyId}`, { method: 'DELETE', token });
      setNotice(t('m35.common.notice', { code: r.policyCode, label: t('m35.common.act.deleted') }));
      load();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m35.policy.notAuthorisedManage'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="policy-repository">
      <h1>{t('m35.policy.title')}</h1>

      <form aria-label="policy-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m35.common.title')}
          <input aria-label="field-policy-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
        </label>
        <label>
          {t('m35.common.type')}
          <select aria-label="field-policy-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {NEW_POLICY_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="submit-policy" disabled={busy}>{busy ? t('m35.common.saving') : t('m35.policy.createBtn')}</Button>
      </form>
      {actionError && <p role="alert" aria-label="policy-action-error">{actionError}</p>}
      {notice && <p aria-label="policy-action-notice">{notice}</p>}

      <div className="filters">
        <label>
          {t('m35.common.type')}
          <select aria-label="filter-type" value={policyType} onChange={(e) => setPolicyType(e.target.value)}>
            {POLICY_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m35.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m35.common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m35.policy.searchPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m35.common.loading')}</p> : (
        <>
          <p aria-label="policy-count">{t('m35.policy.count', { n: total })}</p>
          <DataTable
            ariaLabel="policy-table"
            rows={rows}
            rowKey={(r) => r.policyId}
            columns={[
              { key: 'policyCode', header: t('m35.common.code'), render: (r) => r.policyCode },
              { key: 'title', header: t('m35.common.title'), render: (r) => r.title },
              { key: 'policyType', header: t('m35.common.type'), render: (r) => r.policyType },
              { key: 'version', header: t('m35.common.version'), render: (r) => r.version },
              { key: 'status', header: t('m35.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m35.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.policyCode}`} disabled={busy} onClick={() => void act(r, 'submit', t('m35.common.act.submitted'), 'M35_POLICY_MANAGE')}>{t('m35.common.btnSubmit')}</Button>
                  )}
                  {r.status === 'IN_REVIEW' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.policyCode}`} disabled={busy} onClick={() => void act(r, 'approve', t('m35.common.act.approved'), 'M35_POLICY_APPROVE')}>{t('m35.common.btnApprove')}</Button>
                      <input aria-label={`reason-${r.policyCode}`} value={reasons[r.policyId] ?? ''} onChange={(e) => setReasons((m) => ({ ...m, [r.policyId]: e.target.value }))} placeholder={t('m35.common.reason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.policyCode}`} disabled={busy} onClick={() => void act(r, 'reject', t('m35.common.act.rejected'), 'M35_POLICY_APPROVE', { reason: reasons[r.policyId] ?? '' })}>{t('m35.common.btnReject')}</Button>
                    </>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.policyCode}`} disabled={busy} onClick={() => void act(r, 'publish', t('m35.common.act.published'), 'M35_POLICY_PUBLISH')}>{t('m35.common.btnPublish')}</Button>
                  )}
                  {r.status === 'PUBLISHED' && (
                    <Button variant="secondary" type="button" aria-label={`obsolete-${r.policyCode}`} disabled={busy} onClick={() => void act(r, 'obsolete', t('m35.common.act.obsolete'), 'M35_POLICY_MANAGE')}>{t('m35.common.btnObsolete')}</Button>
                  )}
                  {!TERMINAL.includes(r.status) && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.policyCode}`} disabled={busy} onClick={() => void remove(r)}>{t('m35.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
